import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { AuthImage } from "@/components/AuthImage";
import { ArrowLeft, ShieldCheck, Briefcase, Moon, Search, BadgeAlert } from "lucide-react";
import { Input } from "@/components/ui/input";

const ROLE_LABEL = {
  super_admin: "Platform",
  admin: "Admin",
  office: "Office",
  field: "Field",
};
const ROLE_COLOR = {
  super_admin: "bg-zinc-900 text-white",
  admin: "bg-[#FF5F15] text-white",
  office: "bg-zinc-800 text-white",
  field: "bg-zinc-600 text-white",
};

export default function CrewDirectory() {
  const { user } = useAuth();
  const isField = user.role === "field";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/team/directory");
        if (!cancelled) setRows(r.data);
      } catch (e) { /* non-fatal */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = rows.filter((r) => {
    if (r.suspended) return false;
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return r.name.toLowerCase().includes(s) ||
      r.email.toLowerCase().includes(s) ||
      (r.tomorrow?.title || "").toLowerCase().includes(s);
  });

  return (
    <div data-testid="crew-directory-page" className={`p-6 lg:p-10 max-w-5xl mx-auto ${isField ? "pb-24" : ""}`}>
      <Link to="/app/profile" className="text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] inline-flex items-center gap-1 mb-3" data-testid="crew-back-link">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to profile
      </Link>
      <PageHeader
        tag="/ the crew"
        title="Crew directory"
        subtitle="Everyone in your company, their role, and what they're on tomorrow."
      />

      <div className="hard-card p-3 mb-4 flex items-center gap-2">
        <Search className="w-4 h-4 text-zinc-400 ml-1" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Find by name, email, or job…"
          className="border-0 focus-visible:ring-0 h-9"
          data-testid="crew-search-input"
        />
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-2">{filtered.length} on the team</span>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm p-6">Loading crew…</div>
      ) : filtered.length === 0 ? (
        <div className="hard-card p-8 text-center text-zinc-500 text-sm">Nobody matches that search.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((m) => (
            <CrewCard key={m.id} member={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function CrewCard({ member }) {
  const initials = (member.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  const tomorrow = member.tomorrow;
  return (
    <div className={`hard-card p-4 flex items-start gap-3 ${member.is_me ? "border-[#FF5F15] border-4" : ""}`} data-testid={`crew-card-${member.id}`}>
      <div className="shrink-0">
        {member.avatar_path ? (
          <AuthImage path={member.avatar_path} alt={member.name} className="w-14 h-14 object-cover border-2 border-zinc-900" />
        ) : (
          <div className="w-14 h-14 bg-[#FF5F15] border-2 border-zinc-900 flex items-center justify-center">
            <span className="font-display font-black text-xl text-white">{initials}</span>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-bold uppercase tracking-tight truncate flex items-center gap-1.5">
              {member.name}
              {member.role === "super_admin" && <ShieldCheck className="w-3.5 h-3.5 text-[#FF5F15]" />}
              {member.is_me && <span className="text-[9px] font-mono text-[#FF5F15] uppercase tracking-widest">You</span>}
            </div>
            <div className="text-[11px] text-zinc-500 truncate font-mono">{member.email}</div>
          </div>
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${ROLE_COLOR[member.role] || "bg-zinc-200 text-zinc-700"}`}>
            {ROLE_LABEL[member.role] || member.role}
          </span>
        </div>

        <div className="mt-3 pt-3 border-t border-zinc-200">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Tomorrow</div>
          {!tomorrow ? (
            <div className="text-xs text-zinc-400 italic flex items-center gap-1">
              <BadgeAlert className="w-3.5 h-3.5" /> Not yet scheduled
            </div>
          ) : tomorrow.type === "day_off" ? (
            <div className="text-xs font-bold inline-flex items-center gap-1 text-zinc-600">
              <Moon className="w-3.5 h-3.5" /> Day off
            </div>
          ) : tomorrow.title ? (
            <div className="text-xs font-bold inline-flex items-center gap-1 text-[#FF5F15]">
              <Briefcase className="w-3.5 h-3.5" /> {tomorrow.title}
            </div>
          ) : (
            <div className="text-xs text-zinc-400">Job assigned</div>
          )}
        </div>
      </div>
    </div>
  );
}
